import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const W = 1080;
const H = 1080;

function GraphicPreviewBold({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const { homeTeam, awayTeam, gameDate, gameTime, location } = graphicData;

  const accent = accentBlockColor || teamColors.primary;
  const detailColor = detailTextColor || '#666666';

  const isLightColor = (color) => {
    if (!color || color === 'null') return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 155;
  };

  const getBackground = () => {
    const p = teamColors.primary;
    const s = teamColors.secondary;
    switch (backgroundStyle) {
      case 'radial-gradient': return `radial-gradient(circle at center, ${p} 0%, ${s} 100%)`;
      case 'diagonal-gradient': return `linear-gradient(45deg, ${p} 0%, ${s} 100%)`;
      case 'split-two-tone': return `linear-gradient(90deg, ${p} 0%, ${p} 50%, ${s} 50%, ${s} 100%)`;
      case 'solid-primary': return p;
      case 'solid-secondary': return s;
      default: return `linear-gradient(90deg, ${p} 0%, ${s} 100%)`;
    }
  };

  const getBadgeColor = () => {
    const primaryLight = isLightColor(teamColors.primary);
    const secondaryLight = isLightColor(teamColors.secondary);
    if ((primaryLight && secondaryLight) || secondaryLight) return '#2d3748';
    return '#ffffff';
  };

  const getAwayTeamColor = () => {
    if (isLightColor(teamColors.secondary)) {
      return isLightColor(teamColors.primary) ? '#2d3748' : teamColors.primary;
    }
    return teamColors.secondary;
  };

  const homeNameFontSize = (homeTeam || 'YOUR TEAM').length > 14 ? 140 : (homeTeam || 'YOUR TEAM').length > 9 ? 170 : 210;
  const awayNameFontSize = (awayTeam || 'OPPONENT').length > 14 ? 140 : (awayTeam || 'OPPONENT').length > 9 ? 170 : 210;
  const gameDayFontSize = Math.max(36, Math.min(Math.round(heroFontSize / 3.5), 72));

  const logoCircleStyle = {
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  return (
    <div
      id="graphic-preview"
      style={{
        width: W,
        height: H,
        background: 'white',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: '2px solid #e2e8f0',
        borderRadius: 16,
        fontFamily: 'Barlow, sans-serif',
      }}
    >
      {/* Header */}
      <div
        style={{
          height: 200,
          background: getBackground(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 60px',
          flexShrink: 0,
        }}
      >
        {/* Home Logo */}
        <div style={logoCircleStyle}>
          {homeTeamLogo ? (
            <img
              src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
              alt={homeTeam}
              style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
            />
          ) : (
            <span style={{ fontSize: 64 }}>
              {teamSport === 'Softball' ? '🥎' : '⚾'}
            </span>
          )}
        </div>

        {/* GAME DAY centered */}
        <div
          style={{
            fontFamily: heroFont,
            fontSize: gameDayFontSize,
            fontWeight: 700,
            color: getBadgeColor(),
            letterSpacing: 6,
            textAlign: 'center',
            flex: 1,
            padding: '0 32px',
          }}
        >
          GAME DAY
        </div>

        {/* Opponent Logo */}
        <div style={logoCircleStyle}>
          {awayTeamLogoPreview ? (
            <img
              src={awayTeamLogoPreview}
              alt={awayTeam}
              style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
            />
          ) : (
            <span style={{ fontSize: 64 }}>
              {teamSport === 'Softball' ? '🥎' : '⚾'}
            </span>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '60px 80px',
        }}
      >
        <div
          style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: homeNameFontSize,
            fontWeight: 900,
            color: teamColors.primary,
            lineHeight: 1,
            letterSpacing: 2,
            marginBottom: 24,
            textTransform: 'uppercase',
          }}
        >
          {homeTeam || 'YOUR TEAM'}
        </div>

        <div
          style={{
            fontFamily: 'Barlow, sans-serif',
            fontSize: 48,
            fontWeight: 700,
            color: '#cccccc',
            letterSpacing: 12,
            marginBottom: 24,
          }}
        >
          VS
        </div>

        <div
          style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: awayNameFontSize,
            fontWeight: 900,
            color: getAwayTeamColor(),
            lineHeight: 1,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          {awayTeam || 'OPPONENT'}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          background: '#f7f7f7',
          borderTop: `6px solid ${accent}`,
          padding: '40px 80px',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 48,
            alignItems: 'center',
          }}
        >
          {gameDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%',
                background: accent, flexShrink: 0,
              }} />
              <span style={{
                fontFamily: 'Barlow, sans-serif',
                fontSize: 28, fontWeight: 600, color: detailColor,
              }}>
                {formatGameDate(gameDate, dateFormat)}
              </span>
            </div>
          )}
          {gameTime && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%',
                background: accent, flexShrink: 0,
              }} />
              <span style={{
                fontFamily: 'Barlow, sans-serif',
                fontSize: 28, fontWeight: 600, color: detailColor,
              }}>
                {formatGameTime(gameTime)}
              </span>
            </div>
          )}
          {location && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%',
                background: accent, flexShrink: 0,
              }} />
              <span style={{
                fontFamily: 'Barlow, sans-serif',
                fontSize: 28, fontWeight: 600, color: detailColor,
              }}>
                {location}
              </span>
            </div>
          )}
        </div>

        {showWatermark && (
          <div style={{ position: 'absolute', bottom: 12, right: 12, background: 'rgba(255,255,255,0.92)', padding: '6px 12px', borderRadius: 8, pointerEvents: 'none' }}>
            <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
          </div>
        )}
      </div>
    </div>
  );
}

export default GraphicPreviewBold;
